import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, VStack, Input, Select, FormControl, FormLabel, Button,
} from '@chakra-ui/react';
import { FiActivity } from 'react-icons/fi';
import { upsertStudentStatus } from 'services/qrService';
import StudentPicker from 'views/Dashboard/StudentStatusAdmin/components/StudentPicker';
import StaffPicker from 'components/Search/StaffPicker';
import GuardianPicker from 'components/Search/GuardianPicker';
import { useUserAuth } from 'utils/site';

const SICK_EXIT_TITLES = ['Accidente escolar', 'Enfermedad', 'Otros'];

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Registro rápido de "Salida por Enfermedad" (alumno individual, un solo
// día) desde el dashboard — misma lógica que StudentStatusesTab.js, pero
// acotada a los campos imprescindibles para crear el estado sobre la marcha.
const NewSickExitModal = ({ isOpen, onClose, onSuccess, onError }) => {
  const { user: currentStaffUser } = useUserAuth();
  const [student, setStudent] = useState(null);
  const [title, setTitle] = useState(SICK_EXIT_TITLES[1]);
  const [date, setDate] = useState(today());
  const [responsible, setResponsible] = useState(null);
  const [authorizedBy, setAuthorizedBy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStudent(null);
      setTitle(SICK_EXIT_TITLES[1]);
      setDate(today());
      setResponsible(null);
      setAuthorizedBy(null);
    }
  }, [isOpen]);

  const authorizedByPerson = authorizedBy ?? currentStaffUser;
  const canSave = !!student && !!date && !!responsible && !!authorizedByPerson;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await upsertStudentStatus({
        wp_user_id: student.id,
        rut: student.username,
        type: 'sick_exit',
        reason: title,
        starts_at: `${date}T00:00:00`,
        ends_at: `${date}T23:59:59`,
        authorized_by: authorizedByPerson.name?.trim() || null,
        responsible_name: responsible.name?.trim() || null,
        active: true,
      });
      onSuccess?.(`Salida por enfermedad registrada para ${student.name}`);
      onClose();
    } catch (e) {
      onError?.(e?.response?.data?.errors ? JSON.stringify(e.response.data.errors) : e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={3}>
        <ModalHeader>Salida por enfermedad</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Alumno</FormLabel>
              <StudentPicker value={student} onSelect={(s) => { setStudent(s); setResponsible(null); }} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Título</FormLabel>
              <Select size="sm" borderRadius="8px" value={title} onChange={(e) => setTitle(e.target.value)}>
                {SICK_EXIT_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Fecha</FormLabel>
              <Input type="date" size="sm" borderRadius="8px" value={date} onChange={(e) => setDate(e.target.value)} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Responsable (quien retira)</FormLabel>
              <GuardianPicker metaData={student?.meta_data} value={responsible} onSelect={setResponsible} />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Autorizado por</FormLabel>
              <StaffPicker value={authorizedByPerson} onSelect={setAuthorizedBy} placeholder="Buscar quien autoriza…" />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            variant="educational-primary" size="sm" onClick={handleSave}
            isLoading={saving} isDisabled={!canSave} leftIcon={<FiActivity />}
          >
            Registrar salida
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewSickExitModal;
